/**
 * The ownership chain above an organization — "Rolco · Hans Wilsdorf Foundation
 * · Rolex SA", read from the top down.
 *
 * Walks `parentOrgId` rather than `parentOrgIdAncestors`. The type describes
 * that array as server-computed, but nothing computes it: no admin write path
 * touches it and no trigger exists, so on every document in the collection today
 * it is absent. Reading it would render an empty chain for an organization that
 * plainly has a parent. When Phase 3's trigger lands, this stays correct — it is
 * derived from the same edges the trigger will denormalise.
 */

export interface AncestryNode {
  id: string;
  label: string;
}

export interface OrgLike {
  id: string;
  name?: string;
  displayName?: string;
  parentOrgId?: string;
}

function labelFor(org: OrgLike): string {
  return org.displayName || org.name || org.id;
}

/**
 * Ancestors of `orgId`, FURTHEST first, excluding the organization itself.
 *
 * Stops on a repeat. `forbiddenParentIds` makes a cycle unreachable through the
 * picker, but it cannot unmake one already in the data — a document hand-edited
 * in the console, or written before that guard existed, would otherwise spin
 * this walk forever and hang the editor on open. A truncated chain renders; an
 * infinite loop does not.
 *
 * A `parentOrgId` pointing at a document that no longer exists ends the walk
 * too: the dangling id is reported as its own label, so a broken link is
 * visible rather than silently dropped.
 */
export function ancestryChain(
  orgId: string,
  orgs: ReadonlyArray<OrgLike>,
): { nodes: AncestryNode[]; truncated: boolean } {
  const byId = new Map(orgs.map((o) => [o.id, o]));
  const nodes: AncestryNode[] = [];
  const seen = new Set<string>([orgId]);

  let currentId = byId.get(orgId)?.parentOrgId;
  let truncated = false;
  while (currentId) {
    if (seen.has(currentId)) {
      truncated = true;
      break;
    }
    seen.add(currentId);
    const parent = byId.get(currentId);
    nodes.push({ id: currentId, label: parent ? labelFor(parent) : currentId });
    currentId = parent?.parentOrgId;
  }

  nodes.reverse();
  return { nodes, truncated };
}

/**
 * The chain as it reads on screen, with the organization itself on the end.
 * Returns an empty array when the organization has no parent, so the caller can
 * skip the whole row rather than render a chain of one.
 */
export function ancestryPath(
  org: OrgLike,
  orgs: ReadonlyArray<OrgLike>,
): { nodes: AncestryNode[]; truncated: boolean } {
  const { nodes, truncated } = ancestryChain(org.id, orgs);
  if (nodes.length === 0) return { nodes: [], truncated };
  return { nodes: [...nodes, { id: org.id, label: labelFor(org) }], truncated };
}
